/**
 * Canonical catalog of the Depletions dashboard's sections.
 *
 * This is the single source of truth consumed by BOTH:
 *   1. `DepletionsView` — renders one `CollapsibleSection` per entry.
 *   2. The User Guide (`/user-guide`) — documents one entry per section.
 *
 * Why a catalog instead of two hand-maintained lists: the guide can
 * never fall behind the dashboard. Add a section here and it appears
 * in the product AND in the manual (and in the screenshot capture
 * script's shot list) in the same commit.
 *
 * Adding a section:
 *   1. Append an entry below with a stable `id` (kebab-case — it also
 *      becomes the DOM anchor and the screenshot filename).
 *   2. Write `guide` copy: `what` (what the section shows in plain
 *      English), `how` (numbered steps a new user can follow), and
 *      optional `tips`.
 *   3. Run `npm run guide:capture` to shoot the screenshot and
 *      regenerate the PDF.
 */

import CompareArrowsOutlinedIcon from '@mui/icons-material/CompareArrowsOutlined';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import GridViewOutlinedIcon from '@mui/icons-material/GridViewOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import ShowChartOutlinedIcon from '@mui/icons-material/ShowChartOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import type { ComponentType } from 'react';

import { colors } from '../theme';

/** The five dashboard groups, in the order a user works through them. */
export const DEPLETIONS_CATEGORIES = [
  'Overview & Action',
  'Trends & Performance',
  'Account Intelligence',
  'Base Health',
  'Plan Ahead',
] as const;

export type DepletionsCategory = (typeof DEPLETIONS_CATEGORIES)[number];

/** Accent colour per category — shared by the dashboard and the guide. */
export const CATEGORY_ACCENT: Record<DepletionsCategory, string> = {
  'Overview & Action': colors.chartPrimary,
  'Trends & Performance': colors.info,
  'Account Intelligence': colors.success,
  'Base Health': colors.warning,
  'Plan Ahead': '#8b5cf6',
};

/** Plain-English framing for each group, used as the guide's chapter intro. */
export const CATEGORY_PURPOSE: Record<DepletionsCategory, string> = {
  'Overview & Action': 'Where you start. What happened, and who needs a call.',
  'Trends & Performance': 'The shape of the business over time — by month, product, and state.',
  'Account Intelligence': 'Who your buyers are and how regularly they reorder.',
  'Base Health': 'Whether the account base itself is growing or shrinking.',
  'Plan Ahead': 'Model next quarter before you commit to a number.',
};

/** Guide copy for a section — the "how to use it" half of the catalog. */
export interface SectionGuide {
  /** Plain-English answer to "what am I looking at?". 1–3 sentences. */
  what: string;
  /** Numbered steps. Each is one action a new user can actually take. */
  how: string[];
  /** Optional gotchas / interpretation notes. */
  tips?: string[];
  /**
   * What an administrator can additionally see or do here.
   *
   * Only for behaviour that genuinely differs with the admin role —
   * a note on every topic would say nothing. Rendered as its own
   * callout so a reader can tell "this applies to me" from "this is
   * what my manager sees".
   */
  admin?: string;
}

export interface DepletionsSection {
  /** Stable identifier. Also the DOM anchor + screenshot filename. */
  id: string;
  title: string;
  /** One-line description shown in the dashboard's section header. */
  description: string;
  category: DepletionsCategory;
  Icon: ComponentType<{ sx?: object }>;
  guide: SectionGuide;
}

export const DEPLETIONS_SECTIONS: DepletionsSection[] = [
  {
    id: 'orders',
    title: '2026 Orders',
    description: "Every account's monthly depletions in 2026 — filter by month or recent window",
    category: 'Overview & Action',
    Icon: ReceiptLongOutlinedIcon,
    guide: {
      what: 'What each account bought, month by month, this year.',
      how: [
        'Choose a window: the latest month, the last three months, or the full year.',
        'Open an account to see its months broken down by product.',
      ],
      tips: [
        'Depletions are reported by month, so an "order" is an account\'s monthly total — not a single purchase order. A month with no bar means no order.',
      ],
    },
  },
  {
    id: 'premises-mix',
    title: 'On- vs Off-Premise Mix',
    description: 'How volume splits across on-premise, off-premise, and unclassified accounts',
    category: 'Overview & Action',
    Icon: StorefrontOutlinedIcon,
    guide: {
      what: 'How volume splits across four channels: On-Premise (bars, restaurants), Off-Premise (liquor stores), Online Retailers, and Other (accounts with no channel set).',
      how: [
        'Filter by state to narrow to one market.',
        'Click a tile to list the accounts in that channel.',
      ],
      tips: ['Online Retailers is carved out of Off-Premise, not added on top.'],
    },
  },
  {
    id: 'follow-up-tracker',
    title: 'Follow-Up Tracker',
    description: 'Accounts bucketed by how long since their last depletion — who to call next',
    category: 'Overview & Action',
    Icon: NotificationsActiveOutlinedIcon,
    guide: {
      what: 'Every account bucketed by how long since its last order — who has gone quiet, and how quiet.',
      how: [
        'Click a bucket to load its accounts.',
        'Filter by momentum or state, and sort to lead with your biggest accounts or biggest decliners.',
      ],
      tips: [
        'Inside a bucket, accounts rank by lifetime volume — so the top row is the highest-leverage call.',
      ],
      admin:
        'This is the whole account base, not one territory. A representative sees the same layout narrowed to their own states under Regional Sales → My Territories.',
    },
  },
  {
    id: 'monthly-trend',
    title: 'Monthly Volume Trend',
    description: 'Depletion volume and active-account count over time',
    category: 'Trends & Performance',
    Icon: ShowChartOutlinedIcon,
    guide: {
      what: 'Monthly volume drawn as one line per year, so you can compare the same month across years.',
      how: [
        'Pick which years and which months to show.',
        'Hover a month for its year-over-year breakdown.',
      ],
      tips: [
        'The most recent month is usually partial — check the coverage date at the top of the page.',
      ],
    },
  },
  {
    id: 'product-performance',
    title: 'Product Performance',
    description: 'Volume, share, depth, and momentum by SKU',
    category: 'Trends & Performance',
    Icon: Inventory2OutlinedIcon,
    guide: {
      what: 'How each product is selling: volume, share, reach, depth, and momentum.',
      how: [
        'Sort by volume, momentum, reach, or depth.',
        'Open a SKU to see the detail behind it.',
      ],
      tips: [
        'Reach is how many accounts buy it; depth is the average 6-pk each one takes. Broad-and-thin looks very different from narrow-and-deep.',
      ],
    },
  },
  {
    id: 'state-breakdown',
    title: 'State Breakdown',
    description: 'Where volume concentrates geographically, with per-state momentum',
    category: 'Trends & Performance',
    Icon: PublicOutlinedIcon,
    guide: {
      what: 'Volume by state, with each market compared against prior years.',
      how: [
        'Filter to control states or license states.',
        'Open a state to see its product mix over time.',
      ],
      tips: [
        'In a control (ABC) state the state liquor board is effectively the buyer, so the sales motion runs through listings rather than individual accounts.',
      ],
    },
  },
  {
    id: 'top-accounts',
    title: 'Top Accounts',
    description: 'Your highest-volume retail buyers and their share',
    category: 'Account Intelligence',
    Icon: EmojiEventsOutlinedIcon,
    guide: {
      what: "Your highest-volume buyers and each one's share of the total.",
      how: [
        'Narrow to a single channel with the premises chips.',
        'Open an account to see its products and trend.',
      ],
    },
  },
  {
    id: 'activity-heatmap',
    title: 'Account Activity Heatmap',
    description: 'Per-account monthly volume grid — spot cadence and gaps at a glance',
    category: 'Account Intelligence',
    Icon: GridViewOutlinedIcon,
    guide: {
      what: 'A month-by-month grid of your top accounts, shaded by ordering intensity, so buying rhythms and gaps show up as a pattern.',
      how: [
        "Scan across a row to see one account's cadence.",
        'Change how many accounts and how many months are shown.',
      ],
      tips: [
        'Accounts are grouped by cadence — monthly, bi-monthly, quarterly, infrequent. A blank cell means no order that month.',
      ],
    },
  },
  {
    id: 'new-vs-lost',
    title: 'New vs Lost Accounts',
    description: 'Is the account base growing or shrinking?',
    category: 'Base Health',
    Icon: CompareArrowsOutlinedIcon,
    guide: {
      what: 'How many accounts started buying versus stopped, comparing the recent window against the one before it.',
      how: ['Open either list to see exactly which accounts moved.'],
      tips: [
        '"Lost" means no order in the recent window. An account that orders twice a year can look lost in between.',
      ],
    },
  },
  {
    id: 'growth-decline',
    title: 'Growth & Decline',
    description: 'Returning accounts trending up or down period-over-period',
    category: 'Base Health',
    Icon: TrendingUpOutlinedIcon,
    guide: {
      what: 'Among accounts that bought in both windows, who grew and who shrank — real momentum, separate from accounts arriving or leaving.',
      how: ['Read the growing list to reinforce, and the declining list to rescue.'],
      tips: [
        'A large percentage swing on a tiny account is usually noise. Weigh it against absolute volume.',
      ],
    },
  },
  {
    id: 'what-if',
    title: 'What-If Simulator',
    description: 'Stress-test next quarter across reactivation, new accounts, and frequency',
    category: 'Plan Ahead',
    Icon: TuneOutlinedIcon,
    guide: {
      what: 'A planning model: move the sliders for reactivation, new accounts, order frequency, and product penetration to see the projected effect on volume.',
      how: [
        'Start from a quick scenario, or move one slider at a time to isolate a lever.',
        'Set a target to see what it would take to reach it.',
      ],
      tips: [
        'It extrapolates from current patterns. It knows nothing about pricing changes, new listings, or seasonality.',
      ],
    },
  },
];

/** Sections grouped by category, in catalog order — used by the guide. */
export function depletionsSectionsByCategory(): {
  category: DepletionsCategory;
  purpose: string;
  accent: string;
  sections: DepletionsSection[];
}[] {
  return DEPLETIONS_CATEGORIES.map((category) => ({
    category,
    purpose: CATEGORY_PURPOSE[category],
    accent: CATEGORY_ACCENT[category],
    sections: DEPLETIONS_SECTIONS.filter((s) => s.category === category),
  })).filter((g) => g.sections.length > 0);
}
